/**
 * PersonalInboxTable — message list for the personal inbox.
 *
 * Composes the shared primitives rather than drawing its own rows: `BulkBar`
 * for the header and selection, `MailRow` for every row, with the personal
 * entry from `surfaceContract` supplying the grid, the column set and the
 * action set. Before the revamp was wired this file rendered its own markup,
 * which is how the two inboxes ended up with two different rows and two
 * different ideas of read state.
 *
 * What it still owns: the scroll container, the IntersectionObserver that
 * drives infinite scroll, and the empty/loading states. Those are list
 * concerns, not row concerns.
 *
 * Every row is a `communications` document, so the row reads `communications`
 * field names rather than the per-provider shapes the old multi-hook version
 * had to tolerate.
 */
import React, { useRef, useEffect } from 'react';
import { Loader2, Mail } from 'lucide-react';

import BulkBar from './BulkBar';
import MailRow from './MailRow';
import { SURFACES, rowActionsFor, bulkActionsFor } from './surfaceContract';
import { InboxSkeleton } from './inboxUtils';

export default function PersonalInboxTable({
    entries,
    /** A `useMailSelection` return value — the single source of selection. */
    selection,
    onRowClick,
    /** `(actionKey, mail) => void` for every row and bulk action. */
    onAction,
    folderLabel = 'Inbox',
    isLoading,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
}) {
    const sentinelRef = useRef(null);
    const scrollRef = useRef(null);
    const observerRef = useRef(null);

    // --- IntersectionObserver for infinite scroll ---
    // `root` is the scroll container, not the viewport. With the default
    // (viewport) root, the sentinel only fires when the whole page scrolls —
    // but this list scrolls inside its own box, so the page never moves and
    // the sentinel would sit below the fold forever without intersecting.
    useEffect(() => {
        if (!hasMore || isLoadingMore || !onLoadMore) return undefined;
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;

        observerRef.current?.disconnect();
        const observer = new IntersectionObserver(
            (observed) => {
                if (observed[0]?.isIntersecting) onLoadMore();
            },
            { root: scrollRef.current, rootMargin: '200px' },
        );
        observer.observe(sentinel);
        observerRef.current = observer;
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, onLoadMore, entries.length]);

    if (isLoading && !entries.length) {
        return <InboxSkeleton />;
    }

    if (!entries.length) {
        return (
            /* `flex-1` is load-bearing: this branch returns INSTEAD of the
               table, into the flex row that also holds the folder rail. Without
               it the block shrinks to its own content width and sits hard
               against the rail, which is what "the empty state is broken"
               looked like. `h-full` rather than `h-64` so it centres in the
               space it actually has. */
            <div className="flex-1 flex flex-col items-center justify-center h-full min-h-[16rem] text-stone-400">
                <Mail className="w-12 h-12 mb-3 opacity-40" />
                <p className="font-medium text-stone-600">No messages</p>
                <p className="text-sm mt-1 text-stone-400">
                    Nothing matches this view.
                </p>
            </div>
        );
    }

    // Trash swaps the strip for the two doors out of the bin — derived in
    // `surfaceContract` so both lanes answer the same way. This lane had no
    // Restore at all: its Trash could only be emptied, which is a delay
    // before the same data loss rather than a bin.
    const rowActions = rowActionsFor(SURFACES.personal, { folder: folderLabel });
    // The bulk bar's verbs, derived the same way (#710): folder decides the
    // shape (Trash → Restore/Delete), the selection decides the read
    // direction, and the personal lane is always allowed to select.
    const bulkActions = bulkActionsFor(SURFACES.personal, {
        folder: folderLabel,
        canSelect: true,
        canDelete: true,
        selection,
    });

    return (
        /* min-h-0 — see the note in PersonalInboxSurface. Without it on every
           flex ancestor, the scroll container below is handed the full content
           height and never scrolls. */
        <div className="flex-1 flex flex-col min-h-0">
            <BulkBar
                selection={selection}
                folderLabel={folderLabel}
                gridTemplate={SURFACES.personal.gridTemplate}
                onAction={(key) => onAction?.(key, null, { source: 'bulk' })}
                actions={bulkActions}
            />

            <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
                <div>
                    {entries.map((item) => (
                        <MailRow
                            key={item.comm_id}
                            mail={item}
                            surface={SURFACES.personal}
                            rowActions={rowActions}
                            folder={folderLabel}
                            selected={selection.selectedIds.has(item.comm_id)}
                            onSelect={selection.toggleOne}
                            onOpen={onRowClick}
                            onAction={onAction}
                        />
                    ))}
                </div>

                {hasMore && (
                    <div
                        ref={sentinelRef}
                        className="py-6 flex justify-center"
                        data-testid="inbox-scroll-sentinel"
                    >
                        {isLoadingMore && (
                            <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                        )}
                    </div>
                )}

                {!hasMore && !isLoading && entries.length > 0 && (
                    <div
                        className="py-4 text-center text-sm text-stone-400"
                        data-testid="inbox-all-loaded"
                    >
                        All messages loaded
                    </div>
                )}
            </div>
        </div>
    );
}
